import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateQuote, useMe, useRef } from '../api/hooks';
import { useFormDraft } from '../api/drafts';
import { ChatWidget } from '../components/ChatWidget';
import { RefSelect } from '../components/RefSelect';
import { fmtMoney } from '../lib/format';

interface LineForm {
  itemName: string;
  supplierName: string;
  qty: string;
  unitPrice: string;
  unitCost: string;
  description: string;
}

const emptyLine = (): LineForm => ({ itemName: '', supplierName: '', qty: '1', unitPrice: '', unitCost: '', description: '' });

export function NewQuotePage() {
  const navigate = useNavigate();
  const create = useCreateQuote();
  const me = useMe();
  const vendors = useRef('vendors');
  const customers = useRef('customers');
  const items = useRef('items');
  const taxes = useRef('tax-codes');
  const [customer, setCustomer] = useState({ displayName: '', email: '' });
  const [lines, setLines] = useState<LineForm[]>([emptyLine()]);
  const [expirationDate, setExpirationDate] = useState('');
  const [taxCodeRef, setTaxCodeRef] = useState('');
  const [notes, setNotes] = useState('');

  const setLine = (i: number, patch: Partial<LineForm>) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i: number) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const amountOf = (l: LineForm) => (Number(l.qty) || 0) * (Number(l.unitPrice) || 0);
  const costOf = (l: LineForm) => (Number(l.qty) || 0) * (Number(l.unitCost) || 0);
  const total = lines.reduce((s, l) => s + amountOf(l), 0);
  const totalCost = lines.reduce((s, l) => s + costOf(l), 0);
  const totalMargin = total - totalCost;

  const canSubmit =
    customer.displayName.trim().length > 0 &&
    lines.every((l) => l.itemName.trim() && Number(l.qty) > 0 && l.unitPrice !== '' && l.unitCost !== '');

  const applyExtract = (x: Record<string, unknown>) => {
    if (typeof x.customerName === 'string') setCustomer((c) => ({ ...c, displayName: x.customerName as string }));
    if (typeof x.customerEmail === 'string') setCustomer((c) => ({ ...c, email: x.customerEmail as string }));
    if (Array.isArray(x.lines) && x.lines.length) {
      setLines(
        (x.lines as Record<string, unknown>[]).map((l) => ({
          itemName: typeof l.itemName === 'string' ? l.itemName : '',
          supplierName: typeof l.supplierName === 'string' ? l.supplierName : '',
          qty: l.qty != null ? String(l.qty) : '1',
          unitPrice: l.unitPrice != null ? String(l.unitPrice) : '',
          unitCost: l.unitCost != null ? String(l.unitCost) : '',
          description: typeof l.description === 'string' ? l.description : '',
        })),
      );
    }
  };

  // Server-side autosave draft (persists across pages / refresh; one live quote draft per user).
  const qc = useQueryClient();
  const draftValue = { customer, lines, expirationDate, taxCodeRef, notes };
  const { discard, savedAt } = useFormDraft('quote', draftValue, (p) => {
    const d = p as Partial<typeof draftValue>;
    if (d.customer) setCustomer({ displayName: d.customer.displayName ?? '', email: d.customer.email ?? '' });
    if (Array.isArray(d.lines) && d.lines.length) {
      setLines(
        d.lines.map((l) => ({
          itemName: l.itemName ?? '', supplierName: l.supplierName ?? '', qty: l.qty ?? '1',
          unitPrice: l.unitPrice ?? '', unitCost: l.unitCost ?? '', description: l.description ?? '',
        })),
      );
    }
    if (typeof d.expirationDate === 'string') setExpirationDate(d.expirationDate);
    if (typeof d.taxCodeRef === 'string') setTaxCodeRef(d.taxCodeRef);
    if (typeof d.notes === 'string') setNotes(d.notes);
  });
  const clearDraft = () => {
    void discard();
    qc.invalidateQueries({ queryKey: ['drafts'] });
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        customer: { displayName: customer.displayName, email: customer.email || undefined },
        customerEmail: customer.email || undefined,
        lines: lines.map((l) => ({
          itemName: l.itemName,
          supplierName: l.supplierName || undefined,
          qty: Number(l.qty),
          unitPrice: Number(l.unitPrice),
          unitCost: Number(l.unitCost),
          description: l.description || undefined,
        })),
        taxCodeRef: taxCodeRef || undefined,
        expirationDate: expirationDate || undefined,
        notes: notes || undefined,
      },
      { onSuccess: (q) => { clearDraft(); navigate(`/quotes/${q.id}`); } },
    );
  };

  return (
    <>
      <h1>New Quote</h1>
      <ChatWidget
        formType="quote"
        enabled={me.data?.assistantEnabled ?? false}
        onExtract={applyExtract}
        placeholder="e.g. “Quote for Bearston Law: 10 hrs consulting at $200, our cost $120/hr” — or attach an RFQ / supplier quote."
      />
      <datalist id="vendor-list">{vendors.data?.map((v) => <option key={v.id} value={v.name} />)}</datalist>
      <datalist id="customer-list">{customers.data?.map((c) => <option key={c.id} value={c.name} />)}</datalist>
      <datalist id="item-list">{items.data?.map((it) => <option key={it.id} value={it.name} />)}</datalist>
      <form onSubmit={submit}>
        {create.error && <div className="banner error">{(create.error as Error).message}</div>}

        <div className="card">
          <h2>Customer</h2>
          <div className="row cols-2">
            <div className="field">
              <label>Customer name *</label>
              <input list="customer-list" required value={customer.displayName} onChange={(e) => setCustomer({ ...customer, displayName: e.target.value })} />
            </div>
            <div className="field">
              <label>Customer email</label>
              <input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} />
            </div>
          </div>
          <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>
            New customers, items, and suppliers are created in QuickBooks automatically; existing ones (same name) are reused. Item cost is saved to the item's Purchase Cost.
          </p>
        </div>

        <div className="card">
          <h2>Line items</h2>
          {lines.map((l, i) => {
            const margin = amountOf(l) - costOf(l);
            return (
              <div className="quote-line" key={i}>
                <div className="row cols-2">
                  <div className="field"><label>Item / service *</label><input list="item-list" value={l.itemName} onChange={(e) => setLine(i, { itemName: e.target.value })} placeholder="e.g. Legal Consulting" /></div>
                  <div className="field"><label>Supplier</label><input list="vendor-list" value={l.supplierName} onChange={(e) => setLine(i, { supplierName: e.target.value })} placeholder="QuickBooks vendor or new" /></div>
                </div>
                <div className="line-nums">
                  <div><label>Qty *</label><input type="number" min="0" step="1" value={l.qty} onChange={(e) => setLine(i, { qty: e.target.value })} /></div>
                  <div><label>Unit price *</label><input type="number" min="0" step="0.01" value={l.unitPrice} onChange={(e) => setLine(i, { unitPrice: e.target.value })} /></div>
                  <div><label>Cost / unit *</label><input type="number" min="0" step="0.01" value={l.unitCost} onChange={(e) => setLine(i, { unitCost: e.target.value })} /></div>
                  <div><label>Amount</label><div className="ro-val">{fmtMoney(amountOf(l))}</div></div>
                  <div><label>Margin</label><div className="ro-val" style={{ color: margin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>{fmtMoney(margin)}</div></div>
                  <button type="button" className="link" onClick={() => removeLine(i)} disabled={lines.length === 1}>Remove</button>
                </div>
              </div>
            );
          })}
          <button type="button" onClick={addLine}>+ Add line</button>
          <div className="right" style={{ marginTop: '1rem', lineHeight: 1.7 }}>
            <div className="muted">Total cost: {fmtMoney(totalCost)}</div>
            <div>
              <strong>Total: {fmtMoney(total)}</strong> ·{' '}
              <span style={{ color: totalMargin >= 0 ? 'var(--status-invoiced)' : 'var(--status-rejected)' }}>Margin {fmtMoney(totalMargin)}</span>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="row cols-2">
            <div className="field">
              <label>Expiration date</label>
              <input type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} />
            </div>
            <RefSelect label="Sales tax" value={taxCodeRef} onChange={setTaxCodeRef} options={taxes.data} placeholder="— QuickBooks default —" />
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="btn-row">
          <button className="primary" type="submit" disabled={!canSubmit || create.isPending}>
            {create.isPending ? 'Saving…' : 'Save quote'}
          </button>
          <button type="button" className="link" onClick={() => { clearDraft(); navigate('/'); }}>Discard draft</button>
          <span className="muted">
            Autosaves as a draft as you type. Saving creates the quote (new customer &amp; items go to QuickBooks); pushing it as an estimate is a separate approver Submit step.
            {savedAt ? ' · Draft saved' : ''}
          </span>
        </div>
      </form>
    </>
  );
}
